

















































































import BaseMixin from '@/mixins/BaseMixin';
import Vue from 'vue';
import { Component, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import { LoadingIndicator } from '@/components/loading-indicator/LoadingIndicator';
import { PartCatalogueAssemblyItemDetail } from '@/entities/part-catalogue/PartCatalogueAssemblyItemDetail';
import { PartCatalogueService } from '@/services/part-catalogue-service';
import PartCatalogueDetailsItem from '@/components/part-catalogue/PartCatalogueDetailsItem.vue';
import { LeftArrowIcon } from '@/components/icons/LeftArrowIcon';
import { RightArrowIcon } from '@/components/icons/RightArrowIcon';
import { isNullOrWhitespace } from '@/helper/isNullOrWhitespace';

@Component({
    components: {
        LoadingIndicator,
        PartCatalogueDetailsItem,
        LeftArrowIcon,
        RightArrowIcon,
    },
})
export default class PartCatalogueAssembly extends Mixins(BaseMixin) {
    @Prop()
    private readonly id!: string;

    @Prop()
    private readonly parentGroupId!: string;

    @Prop()
    private readonly previousAssemblyId!: string;

    @Prop()
    private readonly nextAssemblyId!: string;

    @Prop({ default: null, required: false })
    private readonly preSelectedPartId!: string | null;

    @Ref('catalogueImage')
    private readonly catalogueImage!: HTMLElement;

    @Ref('detailItemList')
    private readonly detailItemList!: HTMLElement;

    @Ref('detailItems')
    private readonly itemDetailsRef!: PartCatalogueDetailsItem[];

    private readonly partCatalogueService: PartCatalogueService = new PartCatalogueService();

    private isLoading: boolean = true;
    private hasLoadedImage: boolean = false;

    private isDetailsLoading: boolean = true;

    private catalogueImageHeight: number = 0;
    private catalogueImageHeightMonitor: MutationObserver | null = null;

    private itemDetails: PartCatalogueAssemblyItemDetail[] = [];
    private hotspotDetails: { [key: string]: string[] } = {};

    private activePosition: string = '';

    private get drawingImagePath(): string {
        return this.partCatalogueService.getDrawingUrl(this.id);
    }

    private get itemDetailsListStyle(): { height: string } {
        return {
            height: this.isXlMedia ? `${this.catalogueImageHeight}px` : '100%',
        };
    }

    private isPositionActive(pos: number): boolean {
        return true;
    }

    private async loadItemDetails(): Promise<void> {
        this.itemDetails = await this.partCatalogueService.getItemsOfEntry(this.id);
        const itemDetailsGrouped: { [key: string]: PartCatalogueAssemblyItemDetail[] } = {};

        this.itemDetails.forEach((detail) => {
            const key = this.getHotspotKeyForItemDetail(detail);

            if (!itemDetailsGrouped[key]) {
                itemDetailsGrouped[key] = [];
            }

            itemDetailsGrouped[key].push(detail);
        });

        Object.entries(itemDetailsGrouped).forEach(([key, items]) => {
            this.hotspotDetails[key] = items.map((item) => item.item.assemblyItemId);
        });

        this.itemDetails = Object.entries(itemDetailsGrouped).map(([_, items]) => items[0]);
        this.isDetailsLoading = false;
    }

    private getHotspotKeyForItemDetail(detail: PartCatalogueAssemblyItemDetail): string {
        return `${detail.item.positionInAssembly}:${detail.part.id}`;
    }

    private urlForPartCatalogue(id: string): string {
        return `/PartCatalogue/${id}`;
    }

    private urlForPartCatalogueAssembly(id: string): string {
        return `/PartCatalogue/Assembly/${id}`;
    }

    private async created(): Promise<void> {
        await this.loadItemDetails();
        this.preSelectItem();
    }

    private mounted(): void {
        this.$nextTick(() => {
            this.monitorCatalogueImageHeight();
        });
    }

    private destroyed(): void {
        if (this.catalogueImageHeightMonitor != null) {
            this.catalogueImageHeightMonitor.disconnect();
        }
        window.removeEventListener('resize', this.updateCatalogueImageHeight);
    }

    private onImageLoaded(event: Event): void {
        this.transformDrawing(event);
    }

    private onImageLoadedError(): void {
        this.isLoading = false;
        this.hasLoadedImage = false;
    }

    private transformDrawing(event: Event): void {
        const htmlObject = event.target as HTMLObjectElement;

        if (!htmlObject.contentDocument) {
            return;
        }

        const svg = htmlObject.contentDocument.getElementById('svgRoot');

        if (svg) {
            [...svg.querySelectorAll('.pcg_hotspot')].forEach((e) => {
                const element = e as HTMLElement;
                element.style.cursor = 'pointer';
                element.style.opacity = '0';
                const id = e.id.substring(2);
                e.addEventListener('click', (_) => this.onHotspotClicked(id));
            });

            this.isLoading = false;
            this.hasLoadedImage = true;
        }
    }

    private monitorCatalogueImageHeight(): void {
        this.catalogueImageHeightMonitor = new MutationObserver(this.updateCatalogueImageHeight);
        this.catalogueImageHeightMonitor.observe(this.$el, {
            attributes: true,
            childList: true,
            subtree: true,
        });
        window.addEventListener('resize', this.updateCatalogueImageHeight);
    }

    private updateCatalogueImageHeight(): void {
        const clientHeight = this.catalogueImage && this.catalogueImage.clientHeight;
        if (clientHeight != null && clientHeight !== this.catalogueImageHeight) {
            // -5 is a workaround
            this.catalogueImageHeight = clientHeight - 5;
        }
    }

    private onHotspotClicked(hotspotId: string): void {
        const detailItem = this.itemDetailsRef.find((item) => item.representsHotspot(hotspotId));

        if (detailItem == null) {
            return;
        }

        this.activePosition = detailItem.positionInAssembly;

        this.$scrollTo(detailItem.$el, {
            container: this.isXlMedia ? this.detailItemList : 'body',
            force: true,
            offset: -13,
        });
    }

    private preSelectItem(): void {
        if (isNullOrWhitespace(this.preSelectedPartId)) {
            return;
        }

        const itemDetailToSelect = this.itemDetails.find((item) => item.part.id === this.preSelectedPartId);

        if (itemDetailToSelect == null) {
            return;
        }

        this.onHotspotClicked(itemDetailToSelect.item.assemblyItemId);
    }
}
